import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuFooter } from "@repo/ui/Menu/simpleMenuFooter";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconFilterPlus } from "@tabler/icons-react";
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { DialogTrigger } from "react-aria-components";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { CoachingGoalFilterOption } from "./coachingGoalFilterOption.js";
import { CustomFieldsFilterOption } from "./customFieldsFilterOption.js";
import { FilterContext } from "./filterContext.js";
import type { FilterParams } from "./filterParams.js";
import type { FilterKey, FilterValues } from "./filterState.js";
import { OutcomeFilterOption } from "./outcomeFilterOption.js";
import { PlaybookConditionsFilterOption } from "./playbookConditionsFilterOption.js";
import { buildPlaybookViewFilterDefaultsPatch } from "./playbookViewFilterDefaults.js";
import {
  SetPlaybookFilterDefaultAction,
  SetPlaybookFilterDefaultConfirmation,
} from "./setPlaybookFilterDefaultAction.js";
import { SignalFilterOption } from "./signalFilterOption.js";
import { SignalMetadataFilterOption } from "./signalMetadataFilterOption.js";
import { TotalRecordingDurationFilterOption } from "./totalRecordingDurationFilterOption.js";

type FilterOptionRendererMapping = {
  [F in FilterKey]?: FilterOptionRenderer<F>;
};

type FilterOptionRenderer<F extends FilterKey> = (props: {
  params: FilterParams;
  value: FilterValues[F];
  onChange: (newValue: FilterValues[F]) => void;
}) => ReactNode;

/**
 * Registry of filter keys that show up under the "+ Add filter" menu.
 * Filters not in this map are always-visible chips (e.g. dateRange,
 * teammateGroupIds).
 */
const filterOptionRenderers: FilterOptionRendererMapping = {
  outcome: ({ params, value, onChange }) => (
    <OutcomeFilterOption params={params} value={value} onChange={onChange} />
  ),
  playbookConditions: ({ params, value, onChange }) => (
    <PlaybookConditionsFilterOption
      params={params}
      value={value}
      onChange={onChange}
    />
  ),
  customFields: ({ params, value, onChange }) => (
    <CustomFieldsFilterOption
      params={params}
      value={value}
      onChange={onChange}
    />
  ),
  signalId: ({ value, onChange }) => (
    <SignalFilterOption value={value} onChange={onChange} />
  ),
  signalMetadataFields: ({ params, value, onChange }) => (
    <SignalMetadataFilterOption
      params={params}
      value={value}
      onChange={onChange}
    />
  ),
  totalRecordingDuration: ({ value, onChange }) => (
    <TotalRecordingDurationFilterOption value={value} onChange={onChange} />
  ),
  coachingGoal: ({ value, onChange }) => (
    <CoachingGoalFilterOption value={value} onChange={onChange} />
  ),
};

/** Whether any of the given filters shows up under the "+ Add filter" menu. */
export function checkHasAddableFilters(filters: readonly FilterKey[]): boolean {
  return filters.some((filter) => Boolean(filterOptionRenderers[filter]));
}

export interface AddFilterButtonProps {
  filters: readonly FilterKey[];
  params: FilterParams;
}

export const AddFilterButton: FC<AddFilterButtonProps> = ({
  filters,
  params,
}) => {
  const { values, defaultValues } = useContext(FilterContext);
  const teammateAuthorization = useTeammateAuthorization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { playbookId, view } = params;
  const defaultsPatch = playbookId
    ? buildPlaybookViewFilterDefaultsPatch(
        playbookId,
        filters,
        values,
        defaultValues,
      )
    : undefined;
  const canSetDefault =
    view !== undefined &&
    defaultsPatch !== undefined &&
    teammateAuthorization.can(
      PermissionActions.UPDATE,
      PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES,
    );

  if (!checkHasAddableFilters(filters)) return null;

  return (
    <>
      {playbookId !== undefined && view !== undefined && defaultsPatch ? (
        <SetPlaybookFilterDefaultConfirmation
          playbookId={playbookId}
          view={view}
          defaults={defaultsPatch}
          isOpen={isConfirmationOpen}
          onOpenChange={setIsConfirmationOpen}
        />
      ) : null}

      <DialogTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <TooltipTrigger>
          <IconButton
            size={Size.SMALL}
            variant={ButtonVariant.Secondary}
            accessibleLabel="Add filter"
            icon={<IconFilterPlus className="size-4 text-gray-700" />}
          />
          <SimpleTooltip>Add filter</SimpleTooltip>
        </TooltipTrigger>

        <SimpleMenuPopover
          placement="bottom start"
          footer={
            canSetDefault ? (
              <SimpleMenuFooter>
                <SetPlaybookFilterDefaultAction
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsConfirmationOpen(true);
                  }}
                />
              </SimpleMenuFooter>
            ) : undefined
          }
        >
          <SimpleMenu aria-label="Add filter">
            {filters.map((filter) => (
              <FilterOption key={filter} filter={filter} params={params} />
            ))}
          </SimpleMenu>
        </SimpleMenuPopover>
      </DialogTrigger>
    </>
  );
};

/** Renders the "+ Add filter" entry for the filter identified by the given key. */
const FilterOption = <F extends FilterKey>({
  filter,
  params,
}: {
  filter: F;
  params: FilterParams;
}): ReactNode => {
  const { values, onChange } = useContext(FilterContext);

  const render = filterOptionRenderers[filter];
  const value = values[filter];
  const onValueChange = useCallback(
    (newValue: FilterValues[F]) => {
      onChange({ [filter]: newValue });
    },
    [onChange, filter],
  );

  if (!render) return null;
  return render({ params, value, onChange: onValueChange });
};
